import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import StatCard from '../../components/dashboard/StatCard';
import RevenueChart from '../../components/dashboard/RevenueChart';
import OrdersChart from '../../components/dashboard/OrdersChart';
import DriverStatusPie from '../../components/dashboard/DriverStatusPie';
import RecentOrdersTable from '../../components/dashboard/RecentOrdersTable';
import { orderService } from '../../services/orderService';
import { driverService } from '../../services/driverService';
import { reportService } from '../../services/reportService';
import { FileText, DollarSign, Car, CheckCircle } from 'lucide-react';

const DashboardPage = () => {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        todayOrders: 0,
        totalRevenue: 0,
        activeDrivers: 0,
        customerSatisfaction: 0,
        orderTrends: [],
        revenueData: [],
        driverStatusData: [],
        recentOrders: []
    });
    const [dateRange, setDateRange] = useState('week');
    const [fleetId, setFleetId] = useState(user.user_type === 'admin' ? null : user.fleet_id);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setIsLoading(true);
            try {
                // 獲取今日訂單統計
                const ordersStats = await orderService.getOrdersStats(fleetId);

                // 獲取司機狀態統計
                const driversStats = await driverService.getDriversStats(fleetId);

                // 獲取收入數據
                const revenueData = await reportService.getRevenueReport(fleetId, dateRange);

                // 獲取訂單趨勢
                const orderTrends = await reportService.getOrderTrends(fleetId, dateRange);

                // 獲取最近訂單
                const recentOrders = await orderService.getRecentOrders(fleetId, 5);

                setDashboardData({
                    todayOrders: ordersStats.today_orders,
                    totalRevenue: ordersStats.today_revenue,
                    activeDrivers: driversStats.active_drivers,
                    customerSatisfaction: ordersStats.customer_satisfaction,
                    orderTrends: orderTrends,
                    revenueData: revenueData,
                    driverStatusData: [
                        { name: '在線', value: driversStats.online_drivers, color: '#4ade80' },
                        { name: '行程中', value: driversStats.on_trip_drivers, color: '#3b82f6' },
                        { name: '離線', value: driversStats.offline_drivers, color: '#94a3b8' }
                    ],
                    recentOrders: recentOrders
                });
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDashboardData();
    }, [fleetId, dateRange]);

    const handleDateRangeChange = (e) => {
        setDateRange(e.target.value);
    };

    const handleFleetChange = (e) => {
        setFleetId(e.target.value === 'all' ? null : parseInt(e.target.value));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">儀表板</h1>
                <div className="flex space-x-4">
                    {user.user_type === 'admin' && (
                        <select
                            className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={fleetId || 'all'}
                            onChange={handleFleetChange}
                        >
                            <option value="all">所有車隊</option>
                            {/* 這裡應該從API獲取並填充車隊選項 */}
                            <option value="1">北區車隊</option>
                            <option value="2">中區車隊</option>
                            <option value="3">南區車隊</option>
                        </select>
                    )}
                    <select
                        className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={dateRange}
                        onChange={handleDateRangeChange}
                    >
                        <option value="today">今天</option>
                        <option value="week">本週</option>
                        <option value="month">本月</option>
                        <option value="quarter">本季度</option>
                    </select>
                </div>
            </div>

            {/* 統計卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="今日訂單數"
                    value={dashboardData.todayOrders}
                    icon={FileText}
                    iconBgColor="bg-blue-100"
                    iconColor="text-blue-500"
                    trend={{ value: 12, label: '較昨日' }}
                    trendUp={true}
                />

                <StatCard
                    title="營收總額"
                    value={`$${dashboardData.totalRevenue.toLocaleString()}`}
                    icon={DollarSign}
                    iconBgColor="bg-green-100"
                    iconColor="text-green-500"
                    trend={{ value: 8, label: '較昨日' }}
                    trendUp={true}
                />

                <StatCard
                    title="活躍司機"
                    value={dashboardData.activeDrivers}
                    icon={Car}
                    iconBgColor="bg-purple-100"
                    iconColor="text-purple-500"
                    trend={{ value: 3, label: '較昨日' }}
                    trendUp={false}
                />

                <StatCard
                    title="客戶滿意度"
                    value={`${dashboardData.customerSatisfaction}%`}
                    icon={CheckCircle}
                    iconBgColor="bg-yellow-100"
                    iconColor="text-yellow-500"
                    trend={{ value: 2, label: '較昨日' }}
                    trendUp={true}
                />
            </div>

            {/* 圖表區域 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold">訂單趨勢</h3>
                    </div>
                    <div className="h-60">
                        <OrdersChart data={dashboardData.orderTrends} />
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold">收入分析</h3>
                    </div>
                    <div className="h-60">
                        <RevenueChart data={dashboardData.revenueData} />
                    </div>
                </div>
            </div>

            {/* 司機狀態與最近訂單 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h3 className="font-bold mb-4">司機狀態</h3>
                    <div className="h-60">
                        <DriverStatusPie data={dashboardData.driverStatusData} />
                    </div>
                </div>

                <div className="col-span-1 lg:col-span-2 bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold">最近訂單</h3>
                        <button className="text-blue-500 text-sm hover:underline">查看全部</button>
                    </div>
                    <RecentOrdersTable orders={dashboardData.recentOrders} />
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;